import {
  Cell, CellValue, ChartSpec, ERR, EvalResult, EvalValue, SheetModel, WorkbookModel,
  cellKey, cellName, isErr, parseKey,
} from './types';
import { Node, parseFormula, walkRefs } from './parser';
import { Arg, FUNCS, formatNumber, toBool, toNum, toStr } from './functions';

const MAX_ROWS = 100000;
const MAX_COLS = 1000;

export function emptyWorkbook(): WorkbookModel {
  return { version: 2, sheets: [{ name: 'Sheet1', cells: {}, rows: 1000, cols: 26 }], active: 0 };
}

export class Workbook {
  model: WorkbookModel;
  private valueCache = new Map<string, EvalResult>();
  private usedCache = new Map<number, { rows: number; cols: number }>();
  private astCache = new Map<string, Node | { parseErr: true }>();
  private evalStack = new Set<string>();
  listeners: Array<() => void> = [];

  constructor(model?: WorkbookModel) {
    this.model = model || emptyWorkbook();
    if (!this.model.sheets.length) this.model.sheets.push({ name: 'Sheet1', cells: {}, rows: 1000, cols: 26 });
  }

  notify() {
    this.valueCache.clear();
    this.usedCache.clear();
    for (const fn of this.listeners) fn();
  }

  sheet(i: number): SheetModel {
    return this.model.sheets[Math.max(0, Math.min(i, this.model.sheets.length - 1))];
  }

  sheetIndex(name: string): number {
    const lower = name.toLowerCase();
    return this.model.sheets.findIndex((s) => s.name.toLowerCase() === lower);
  }

  addSheet(name?: string): number {
    let n = name;
    if (!n) {
      let i = this.model.sheets.length + 1;
      while (this.sheetIndex(`Sheet${i}`) !== -1) i++;
      n = `Sheet${i}`;
    }
    if (this.sheetIndex(n) !== -1) {
      let i = 2;
      while (this.sheetIndex(`${n} ${i}`) !== -1) i++;
      n = `${n} ${i}`;
    }
    this.model.sheets.push({ name: n, cells: {}, rows: 1000, cols: 26 });
    this.notify();
    return this.model.sheets.length - 1;
  }

  removeSheet(i: number) {
    if (this.model.sheets.length <= 1) return;
    this.model.sheets.splice(i, 1);
    this.notify();
  }

  renameSheet(i: number, name: string): boolean {
    const clean = name.trim().slice(0, 60);
    if (!clean || this.sheetIndex(clean) !== -1) return false;
    this.sheet(i).name = clean;
    this.notify();
    return true;
  }

  getCell(si: number, r: number, c: number): Cell | undefined {
    return this.sheet(si).cells[cellKey(r, c)];
  }

  setCellInput(si: number, r: number, c: number, input: string) {
    if (r < 0 || c < 0 || r >= MAX_ROWS || c >= MAX_COLS) return;
    const sheet = this.sheet(si);
    const key = cellKey(r, c);
    const trimmed = input;
    if (trimmed === '') {
      const existing = sheet.cells[key];
      if (existing) {
        delete existing.v;
        delete existing.f;
        if (!existing.s) delete sheet.cells[key];
      }
      this.notify();
      return;
    }
    const cell: Cell = sheet.cells[key] || {};
    if (trimmed.startsWith('=') && trimmed.length > 1) {
      cell.f = trimmed.slice(1);
      delete cell.v;
    } else {
      delete cell.f;
      cell.v = coerceInput(trimmed);
    }
    sheet.cells[key] = cell;
    if (r >= (sheet.rows || 0)) sheet.rows = r + 1;
    if (c >= (sheet.cols || 0)) sheet.cols = c + 1;
    this.notify();
  }

  setCells(si: number, updates: Array<{ r: number; c: number; input?: string; v?: CellValue; f?: string; s?: Cell['s'] }>) {
    const sheet = this.sheet(si);
    for (const u of updates) {
      if (u.r < 0 || u.c < 0 || u.r >= MAX_ROWS || u.c >= MAX_COLS) continue;
      const key = cellKey(u.r, u.c);
      if (u.input !== undefined) {
        const cell: Cell = sheet.cells[key] || {};
        if (u.input === '') {
          delete cell.v;
          delete cell.f;
        } else if (u.input.startsWith('=') && u.input.length > 1) {
          cell.f = u.input.slice(1);
          delete cell.v;
        } else {
          delete cell.f;
          cell.v = coerceInput(u.input);
        }
        if (u.s) cell.s = { ...(cell.s || {}), ...u.s };
        if (cell.v === undefined && cell.f === undefined && !cell.s) delete sheet.cells[key];
        else sheet.cells[key] = cell;
      } else {
        const cell: Cell = sheet.cells[key] || {};
        if (u.f !== undefined) {
          cell.f = u.f.startsWith('=') ? u.f.slice(1) : u.f;
          delete cell.v;
        } else if (u.v !== undefined) {
          cell.v = u.v;
          delete cell.f;
        }
        if (u.s) cell.s = { ...(cell.s || {}), ...u.s };
        sheet.cells[key] = cell;
      }
      if (u.r >= (sheet.rows || 0)) sheet.rows = u.r + 1;
      if (u.c >= (sheet.cols || 0)) sheet.cols = u.c + 1;
    }
    this.notify();
  }

  clearRange(si: number, sr: number, sc: number, er: number, ec: number) {
    const sheet = this.sheet(si);
    for (let r = sr; r <= er; r++) {
      for (let c = sc; c <= ec; c++) delete sheet.cells[cellKey(r, c)];
    }
    this.notify();
  }

  applyStyle(si: number, sr: number, sc: number, er: number, ec: number, style: NonNullable<Cell['s']>) {
    const sheet = this.sheet(si);
    for (let r = sr; r <= er; r++) {
      for (let c = sc; c <= ec; c++) {
        const key = cellKey(r, c);
        const cell = sheet.cells[key] || {};
        cell.s = { ...(cell.s || {}), ...style };
        sheet.cells[key] = cell;
      }
    }
    this.notify();
  }

  usedRange(si: number): { rows: number; cols: number } {
    const cached = this.usedCache.get(si);
    if (cached) return cached;
    const sheet = this.sheet(si);
    let rows = 0;
    let cols = 0;
    for (const key of Object.keys(sheet.cells)) {
      const [r, c] = parseKey(key);
      if (r + 1 > rows) rows = r + 1;
      if (c + 1 > cols) cols = c + 1;
    }
    const out = { rows, cols };
    this.usedCache.set(si, out);
    return out;
  }

  getValue(si: number, r: number, c: number): EvalValue {
    const sheet = this.sheet(si);
    const key = sheet.name + ' ' + cellKey(r, c);
    const cached = this.valueCache.get(key);
    if (cached !== undefined) return flattenScalar(cached);
    const cell = sheet.cells[cellKey(r, c)];
    if (!cell) return null;
    if (cell.f === undefined) return cell.v ?? null;
    if (this.evalStack.has(key)) return ERR.CYCLE;
    this.evalStack.add(key);
    let result: EvalResult;
    try {
      result = this.evalFormulaAt(cell.f, si, r, c);
    } catch {
      result = ERR.NAME;
    } finally {
      this.evalStack.delete(key);
    }
    this.valueCache.set(key, result);
    return flattenScalar(result);
  }

  evalFormulaAt(src: string, si: number, r: number, c: number): EvalResult {
    let ast = this.astCache.get(src);
    if (!ast) {
      try {
        ast = parseFormula(src);
      } catch {
        ast = { parseErr: true };
      }
      this.astCache.set(src, ast);
    }
    if ('parseErr' in ast && (ast as any).parseErr) return ERR.NAME;
    return this.evalNode(ast as Node, si, r, c);
  }

  private resolveSheet(name: string | undefined, si: number): number {
    if (!name) return si;
    const idx = this.sheetIndex(name);
    return idx;
  }

  private rangeValues(si: number, sr: number, sc: number, er: number, ec: number): EvalValue[][] {
    let endR = er;
    if (endR === -1) endR = Math.max(this.usedRange(si).rows - 1, sr);
    endR = Math.min(endR, MAX_ROWS - 1);
    const out: EvalValue[][] = [];
    for (let r = sr; r <= endR; r++) {
      const row: EvalValue[] = [];
      for (let c = sc; c <= Math.min(ec, MAX_COLS - 1); c++) row.push(this.getValue(si, r, c));
      out.push(row);
    }
    return out;
  }

  private evalNode(node: Node, si: number, r: number, c: number): EvalResult {
    switch (node.t) {
      case 'num': return node.v;
      case 'str': return node.v;
      case 'bool': return node.v;
      case 'err': return { err: node.v };
      case 'ref': {
        const target = this.resolveSheet(node.sheet, si);
        if (target === -1) return ERR.REF;
        return this.getValue(target, node.r, node.c);
      }
      case 'range': {
        const target = this.resolveSheet(node.sheet, si);
        if (target === -1) return ERR.REF;
        return this.rangeValues(target, node.sr, node.sc, node.er, node.ec);
      }
      case 'pct': {
        const n = toNum(this.evalNode(node.x, si, r, c) as Arg);
        return typeof n === 'number' ? n / 100 : n;
      }
      case 'un': {
        const v = this.evalNode(node.x, si, r, c);
        const n = toNum(v as Arg);
        if (typeof n !== 'number') return n;
        return node.op === '-' ? -n : n;
      }
      case 'bin': return this.evalBin(node, si, r, c);
      case 'call': return this.evalCall(node, si, r, c);
    }
  }

  private evalBin(node: Extract<Node, { t: 'bin' }>, si: number, r: number, c: number): EvalResult {
    const lr = this.evalNode(node.l, si, r, c);
    const rr = this.evalNode(node.r, si, r, c);
    if (Array.isArray(lr) || Array.isArray(rr)) {
      const lg = Array.isArray(lr) ? lr : [[lr]];
      const rg = Array.isArray(rr) ? rr : [[rr]];
      const rows = Math.max(lg.length, rg.length);
      const cols = Math.max(lg[0]?.length || 0, rg[0]?.length || 0);
      const out: EvalValue[][] = [];
      for (let i = 0; i < rows; i++) {
        const row: EvalValue[] = [];
        for (let j = 0; j < cols; j++) {
          const lv = lg[lg.length === 1 ? 0 : i]?.[lg[0]?.length === 1 ? 0 : j];
          const rv2 = rg[rg.length === 1 ? 0 : i]?.[rg[0]?.length === 1 ? 0 : j];
          row.push(lv === undefined || rv2 === undefined ? ERR.NA : binScalar(node.op, lv, rv2));
        }
        out.push(row);
      }
      return out;
    }
    return binScalar(node.op, lr as EvalValue, rr as EvalValue);
  }

  getDisplay(si: number, r: number, c: number): string {
    const v = this.getValue(si, r, c);
    if (v == null) return '';
    if (isErr(v)) return v.err;
    const cell = this.getCell(si, r, c);
    if (typeof v === 'number') {
      if (cell?.s?.numFmt) return formatNumber(v, cell.s.numFmt);
      if (Number.isInteger(v) && Math.abs(v) < 1e15) return String(v);
      const s = String(v);
      return s.length > 12 ? String(Number(v.toPrecision(10))) : s;
    }
    if (typeof v === 'boolean') return v ? 'TRUE' : 'FALSE';
    return String(v);
  }

  private evalCall(node: Extract<Node, { t: 'call' }>, si: number, r: number, c: number): EvalResult {
    const name = node.name;
    if (name === 'IF') {
      const cond = toBool(flattenScalar(this.evalNode(node.args[0], si, r, c)) as Arg);
      if (typeof cond !== 'boolean') return cond;
      if (cond) return node.args.length > 1 ? this.evalNode(node.args[1], si, r, c) : true;
      return node.args.length > 2 ? this.evalNode(node.args[2], si, r, c) : false;
    }
    if (name === 'IFS') {
      for (let i = 0; i + 1 < node.args.length; i += 2) {
        const cond = toBool(flattenScalar(this.evalNode(node.args[i], si, r, c)) as Arg);
        if (typeof cond !== 'boolean') return cond;
        if (cond) return this.evalNode(node.args[i + 1], si, r, c);
      }
      return ERR.NA;
    }
    if (name === 'IFERROR' || name === 'IFNA') {
      const v = this.evalNode(node.args[0], si, r, c);
      const scalar = flattenScalar(v);
      const isTarget = isErr(scalar) && (name === 'IFERROR' || scalar.err === '#N/A');
      if (isTarget) return node.args.length > 1 ? this.evalNode(node.args[1], si, r, c) : '';
      return v;
    }
    if (name === 'ROW') {
      if (!node.args.length) return r + 1;
      const arg = node.args[0];
      if (arg.t === 'ref') return arg.r + 1;
      if (arg.t === 'range') return arg.sr + 1;
      return ERR.VALUE;
    }
    if (name === 'COLUMN') {
      if (!node.args.length) return c + 1;
      const arg = node.args[0];
      if (arg.t === 'ref') return arg.c + 1;
      if (arg.t === 'range') return arg.sc + 1;
      return ERR.VALUE;
    }
    const fn = FUNCS[name];
    if (!fn) return ERR.NAME;
    const args: Arg[] = node.args.map((a) => this.evalNode(a, si, r, c) as Arg);
    return fn(args) as EvalResult;
  }

  getInput(si: number, r: number, c: number): string {
    const cell = this.getCell(si, r, c);
    if (!cell) return '';
    if (cell.f !== undefined) return '=' + cell.f;
    if (cell.v == null) return '';
    if (typeof cell.v === 'boolean') return cell.v ? 'TRUE' : 'FALSE';
    return String(cell.v);
  }

  rangeDisplayGrid(si: number, sr: number, sc: number, er: number, ec: number): string[][] {
    const out: string[][] = [];
    for (let r = sr; r <= er; r++) {
      const row: string[] = [];
      for (let c = sc; c <= ec; c++) row.push(this.getDisplay(si, r, c));
      out.push(row);
    }
    return out;
  }

  charts(si: number): ChartSpec[] {
    return this.sheet(si).charts || [];
  }

  addChart(si: number, spec: Omit<ChartSpec, 'id'> & { id?: string }) {
    const sheet = this.sheet(si);
    if (!sheet.charts) sheet.charts = [];
    const maxN = sheet.charts.reduce((a, ch) => Math.max(a, Number(/_(\d+)$/.exec(ch.id)?.[1] || 0)), 0);
    const id = spec.id || 'ch_' + (maxN + 1);
    sheet.charts.push({ ...spec, id });
    this.notify();
  }

  removeChart(si: number, id: string) {
    const sheet = this.sheet(si);
    sheet.charts = (sheet.charts || []).filter((ch) => ch.id !== id);
    this.notify();
  }

  toJSON(): WorkbookModel {
    return this.model;
  }
}

function binScalar(op: string, l: EvalValue, rv: EvalValue): EvalValue {
  if (isErr(l)) return l;
  if (isErr(rv)) return rv;
  if (op === '&') {
    const ls = toStr(l as Arg);
    const rs = toStr(rv as Arg);
    if (typeof ls !== 'string') return ls;
    if (typeof rs !== 'string') return rs;
    return ls + rs;
  }
  if (op === '+' || op === '-' || op === '*' || op === '/' || op === '^') {
    const ln = toNum(l as Arg);
    const rn = toNum(rv as Arg);
    if (typeof ln !== 'number') return ln;
    if (typeof rn !== 'number') return rn;
    switch (op) {
      case '+': return ln + rn;
      case '-': return ln - rn;
      case '*': return ln * rn;
      case '/': return rn === 0 ? ERR.DIV0 : ln / rn;
      case '^': return Math.pow(ln, rn);
    }
  }
  const cmp = compareVals(l, rv);
  switch (op) {
    case '=': return cmp === 0;
    case '<>': return cmp !== 0;
    case '<': return cmp < 0;
    case '>': return cmp > 0;
    case '<=': return cmp <= 0;
    case '>=': return cmp >= 0;
  }
  return ERR.VALUE;
}

function flattenScalar(v: EvalResult): EvalValue {
  if (Array.isArray(v)) return v[0]?.[0] ?? null;
  return v;
}

function compareVals(a: EvalValue, b: EvalValue): number {
  if (a == null && b == null) return 0;
  // Excel: blank compares as 0 against numbers and as '' against strings.
  if (a == null && typeof b === 'number') return 0 - b;
  if (b == null && typeof a === 'number') return a - 0;
  const an = typeof a === 'number';
  const bn = typeof b === 'number';
  if (an && bn) return (a as number) - (b as number);
  if (typeof a === 'boolean' || typeof b === 'boolean') {
    const av = a === true ? 1 : a === false ? 0 : typeof a === 'number' ? (a as number) : NaN;
    const bv = b === true ? 1 : b === false ? 0 : typeof b === 'number' ? (b as number) : NaN;
    if (!Number.isNaN(av) && !Number.isNaN(bv)) return av - bv;
  }
  if (a == null) return b === '' || b == null ? 0 : -1;
  if (b == null) return a === '' || a == null ? 0 : 1;
  const as = String(an ? a : a).toLowerCase();
  const bs = String(bn ? b : b).toLowerCase();
  return as < bs ? -1 : as > bs ? 1 : 0;
}

export function coerceInput(input: string): CellValue {
  const t = input.trim();
  if (t === '') return input;
  if (/^-?\$?[\d,]*\.?\d+(?:[eE][+-]?\d+)?%?$/.test(t)) {
    const pct = t.endsWith('%');
    const n = Number(t.replace(/[$,%]/g, ''));
    if (Number.isFinite(n)) return pct ? n / 100 : n;
  }
  const up = t.toUpperCase();
  if (up === 'TRUE') return true;
  if (up === 'FALSE') return false;
  return input;
}

export function parseWorkbookData(data: string): WorkbookModel {
  if (!data || !data.trim()) return emptyWorkbook();
  let parsed: any;
  try {
    parsed = JSON.parse(data);
  } catch {
    return emptyWorkbook();
  }
  if (parsed && parsed.version === 2 && Array.isArray(parsed.sheets)) {
    // Sanitize untrusted artifact JSON — bad metadata here crashes metrics/rendering.
    parsed.sheets = parsed.sheets.filter((s: any) => s && typeof s === 'object');
    parsed.sheets.forEach((s: any, i: number) => {
      if (typeof s.name !== 'string' || !s.name) s.name = `Sheet${i + 1}`;
      if (!s.cells || typeof s.cells !== 'object' || Array.isArray(s.cells)) s.cells = {};
      s.rows = Number.isFinite(Number(s.rows)) ? Math.max(0, Math.min(Number(s.rows), 100000)) : 0;
      s.cols = Number.isFinite(Number(s.cols)) ? Math.max(0, Math.min(Number(s.cols), 1000)) : 0;
      for (const dim of ['colW', 'rowH'] as const) {
        if (s[dim] && typeof s[dim] === 'object') {
          for (const k of Object.keys(s[dim])) {
            const n = Number(s[dim][k]);
            if (!Number.isFinite(n) || n <= 0 || n > 2000) delete s[dim][k];
            else s[dim][k] = n;
          }
        } else {
          delete s[dim];
        }
      }
      if (!Array.isArray(s.charts)) delete s.charts;
      else s.charts = s.charts.filter((ch: any) => ch && typeof ch === 'object' && typeof ch.range === 'string');
    });
    if (!parsed.sheets.length) return emptyWorkbook();
    return parsed as WorkbookModel;
  }
  const legacySheets = Array.isArray(parsed) ? parsed : Array.isArray(parsed?.sheets) ? parsed.sheets : parsed?.rows ? [parsed] : [];
  if (!legacySheets.length) return emptyWorkbook();
  const sheets: SheetModel[] = legacySheets.map((ls: any, i: number) => {
    const cells: Record<string, Cell> = {};
    const rows = ls?.rows || {};
    let maxR = 0;
    let maxC = 0;
    Object.entries(rows).forEach(([rk, row]: [string, any]) => {
      const r = Number(rk);
      if (!Number.isFinite(r)) return;
      Object.entries(row?.cells || {}).forEach(([ck, cell]: [string, any]) => {
        const c = Number(ck);
        if (!Number.isFinite(c)) return;
        const text = cell?.text;
        if (text == null || text === '') return;
        const str = String(text);
        if (str.startsWith('=') && str.length > 1) cells[cellKey(r, c)] = { f: str.slice(1) };
        else cells[cellKey(r, c)] = { v: coerceInput(str) };
        maxR = Math.max(maxR, r + 1);
        maxC = Math.max(maxC, c + 1);
      });
    });
    return { name: String(ls?.name || `Sheet${i + 1}`), cells, rows: Math.max(maxR, 200), cols: Math.max(maxC, 26) };
  });
  return { version: 2, sheets, active: 0 };
}

export { cellName };
